import "@/engine/viewer/core/three-bvh-setup";
import { GAIA_CAMERA_POSITION, GAIA_FOV } from "@/utils/cameraUtil";
import { Canvas } from "@react-three/fiber";
import React, { useState } from "react";
import {
  BACKGROUND_INTENSITY,
  ENVIRONMENT_INTENSITY_CONSTANT,
  GAIA_AMBIENT_LIGHT_COLOR,
  GAIA_AMBIENT_LIGHT_INTENSITY,
  GAIA_AMBIENT_LIGHT_INTENSITY_CONSTANT,
  GAIA_DIRECTIONAL_LIGHT_INTENSITY,
  GAIA_DIRECTIONAL_LIGHT_INTENSITY_CONSTANT,
} from "@/utils/gaia-constants";
import { HengeDepthOfField } from "@/engine/viewer/postprocessing/effects/HengeDepthOfField";
import { HengeLUT } from "@/engine/viewer/postprocessing/effects/HengeLUT";
import { HengeToneMapping } from "@/engine/viewer/postprocessing/effects/HengeToneMapping";
import {
  LightPropsDto,
  VaultObjectFilter,
} from "@/types/vault-object-filter-types";
import { ToneMappingMode } from "postprocessing";
import { PreloadAssets } from "@/engine/viewer/PreloadAssets";
import CanvasIdSetter from "@/engine/viewer/CanvasIdSetter";
import {
  AdaptiveDpr,
  CameraControls,
  Environment,
  PerformanceMonitor,
  Stats,
} from "@react-three/drei";
import { Lights, shadowBias, shadowNormalBias } from "@/engine/viewer/Lights";
import {
  Bloom,
  BrightnessContrast,
  EffectComposer,
  Noise,
  Outline,
  ToneMapping,
} from "@react-three/postprocessing";
import useGaiaStoreContainer from "@/engine/viewer/core/useGaiaStoreContainer";

const isDev = process.env.NODE_ENV === "development";

interface Props {
  id: string;
  children?: React.ReactElement[] | React.ReactElement;
  onDoubleClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  filter?: VaultObjectFilter;
  lights?: LightPropsDto[];
}

const Viewer = ({ id, children, onDoubleClick, filter, lights }: Props) => {
  const [degraded, degrade] = useState(false);
  const selectedLayerObject = useGaiaStoreContainer(
    (state) => state.selectedLayerObject,
  );

  return (
    <div
      className={
        "henge-canvas-wrapper relative block w-full h-full overflow-hidden"
      }
    >
      <Canvas
        onDoubleClick={(e) => {
          onDoubleClick?.(e);
        }}
        gl={{
          alpha: true,
          antialias: true,
          precision: "lowp",
          powerPreference: "low-power",
          preserveDrawingBuffer: true,
          logarithmicDepthBuffer: true,
          localClippingEnabled: true,
        }}
        performance={{
          current: 1,
          min: 0.1,
          max: 1,
          debounce: 200,
        }}
        shadows
        camera={{
          fov: GAIA_FOV,
          near: 0.01,
          far: 12000,
          position: GAIA_CAMERA_POSITION,
          zoom: 0.01,
        }}
        // orthographic
      >
        {isDev && <Stats />}

        <CanvasIdSetter id={id} />

        <AdaptiveDpr pixelated />
        <PerformanceMonitor onDecline={() => degrade(true)} />

        {filter?.lightPreset?.environment ? (
          <Environment
            frames={degraded ? 1 : Infinity}
            resolution={1024}
            environmentIntensity={
              filter.lightPreset.environment.environmentIntensity
            }
            backgroundIntensity={0}
            files={`${process.env.ASSET_URL ?? ""}/assets/environments/hdri/${filter.lightPreset.environment.files}`}
          />
        ) : (
          <Environment
            frames={degraded ? 1 : Infinity}
            resolution={1024}
            environmentIntensity={
              BACKGROUND_INTENSITY * ENVIRONMENT_INTENSITY_CONSTANT
            }
            backgroundIntensity={0}
            files={`${process.env.ASSET_URL ?? ""}/assets/environments/hdri/studio_small_09_1k.hdr`}
          />
        )}

        <group name={"SceneLightGroup"}>
          {lights ? (
            <Lights lights={lights} />
          ) : (
            <>
              <ambientLight
                args={[
                  GAIA_AMBIENT_LIGHT_COLOR,
                  BACKGROUND_INTENSITY *
                    GAIA_AMBIENT_LIGHT_INTENSITY_CONSTANT *
                    GAIA_AMBIENT_LIGHT_INTENSITY,
                ]}
                name={"DefaultAmbientLight"}
              />
              <directionalLight
                args={[
                  GAIA_AMBIENT_LIGHT_COLOR,
                  BACKGROUND_INTENSITY *
                    GAIA_DIRECTIONAL_LIGHT_INTENSITY_CONSTANT *
                    GAIA_DIRECTIONAL_LIGHT_INTENSITY,
                ]}
                position={[4, 3, 2]}
                castShadow
                shadow-bias={shadowBias}
                shadow-normalBias={shadowNormalBias}
                shadow-camera-left={-8}
                shadow-camera-top={8}
                shadow-camera-right={8}
                shadow-camera-bottom={-8}
                shadow-camera-near={0.1}
                shadow-camera-far={20}
                shadow-mapSize-width={4096}
                shadow-mapSize-height={4096}
                name={"DefaultShadowLight"}
              />
            </>
          )}
        </group>

        <CameraControls makeDefault />

        {children}

        <EffectComposer
          resolutionScale={0.5}
          multisampling={1}
          autoClear={false}
        >
          <>
            {filter?.postprocessing.effects.DepthOfField && (
              <HengeDepthOfField
                {...filter.postprocessing.effects.DepthOfField}
              />
            )}

            {filter?.postprocessing.effects.Noise && (
              <Noise
                {...filter.postprocessing.effects.Noise}
                opacity={
                  filter.postprocessing.effects.Noise.opacity
                    ? filter.postprocessing.effects.Noise.opacity *
                      filter.strength
                    : undefined
                }
              />
            )}

            {filter?.postprocessing.effects.ToneMapping ? (
              <HengeToneMapping
                {...filter.postprocessing.effects.ToneMapping}
                strength={filter.strength}
              />
            ) : (
              <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
            )}

            {filter?.postprocessing.effects.LUT && (
              <HengeLUT
                {...filter.postprocessing.effects.LUT}
                strength={filter.strength}
              />
            )}

            {filter?.postprocessing.effects.Bloom && (
              <Bloom {...filter.postprocessing.effects.Bloom} />
            )}

            <Outline
              selection={selectedLayerObject ? [selectedLayerObject] : []}
              // kernelSize={KernelSize.LARGE}
              // blendFunction={BlendFunction.ALPHA}
              edgeStrength={10}
              // visibleEdgeColor={0x6677dd}
              visibleEdgeColor={0x4455aa}
              // hiddenEdgeColor={0x6677dd}
              hiddenEdgeColor={0x4455aa}
              blur
            />

            {filter?.postprocessing.effects.BrightnessContrast && (
              <BrightnessContrast
                brightness={
                  filter.postprocessing.effects.BrightnessContrast.brightness
                    ? filter.postprocessing.effects.BrightnessContrast
                        .brightness * filter.strength
                    : undefined
                }
                contrast={
                  filter.postprocessing.effects.BrightnessContrast.contrast
                    ? filter.postprocessing.effects.BrightnessContrast
                        .contrast * filter.strength
                    : undefined
                }
              />
            )}
          </>
        </EffectComposer>

        <React.Suspense>
          <PreloadAssets />
        </React.Suspense>
      </Canvas>
    </div>
  );
};

export default Viewer;
